'use client';

import { useCallback, useEffect, useState } from 'react';
import type { Area } from 'react-easy-crop';
import { RotateCcw, RotateCw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { getCroppedImageBlob } from '@/features/lexical/utils/get-cropped-image-blob';
import { PROFILE_BANNER_CROP_ASPECT } from '@/features/profile/constants/image-crop';
import { cn } from '@/lib/utils';

import { AvatarCropStage } from './avatar-crop-stage';
import { BannerCropStage } from './banner-crop-stage';
import { BannerPreviewPanel } from './banner-preview-panel';
import type { ProfileImageCropVariant } from './types';

export type { ProfileImageCropVariant } from './types';

interface ProfileImageCropDialogProps {
  open: boolean;
  imageSrc: string;
  variant: ProfileImageCropVariant;
  aspect: number;
  isSubmitting: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (blob: Blob) => Promise<void>;
}

/**
 * Frame selection after picking a profile avatar or banner.
 * Both variants: movable + resizable frame (VK-like logic), light theme.
 */
export function ProfileImageCropDialog({
  open,
  imageSrc,
  variant,
  aspect,
  isSubmitting,
  onOpenChange,
  onConfirm,
}: ProfileImageCropDialogProps): React.JSX.Element {
  const [rotation, setRotation] = useState(0);
  const [selectionPixels, setSelectionPixels] = useState<Area | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isPreview, setIsPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isBuildingPreview, setIsBuildingPreview] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }
    setRotation(0);
    setSelectionPixels(null);
    setError(null);
    setIsPreview(false);
    setPreviewUrl((prev) => {
      if (prev) {
        URL.revokeObjectURL(prev);
      }
      return null;
    });
  }, [open, imageSrc]);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const buildCroppedBlob = useCallback(async () => {
    if (!selectionPixels) {
      throw new Error('Выберите область изображения');
    }
    return getCroppedImageBlob({
      imageSrc,
      pixelCrop: selectionPixels,
      rotation,
      mimeType: 'image/webp',
      quality: 0.9,
    });
  }, [imageSrc, rotation, selectionPixels]);

  const handleDone = useCallback(async () => {
    setError(null);
    try {
      const blob = await buildCroppedBlob();
      await onConfirm(blob);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось обрезать изображение');
    }
  }, [buildCroppedBlob, onConfirm]);

  const handlePreview = useCallback(async () => {
    setError(null);
    setIsBuildingPreview(true);
    try {
      const blob = await buildCroppedBlob();
      const nextUrl = URL.createObjectURL(blob);
      setPreviewUrl((prev) => {
        if (prev) {
          URL.revokeObjectURL(prev);
        }
        return nextUrl;
      });
      setIsPreview(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось построить предпросмотр');
    } finally {
      setIsBuildingPreview(false);
    }
  }, [buildCroppedBlob]);

  const title = variant === 'avatar' ? 'Фотография профиля' : 'Редактирование фона профиля';
  const description =
    variant === 'avatar'
      ? 'Выбранная область будет выводиться на странице профиля. Если изображение ориентировано неправильно, фотографию можно повернуть.'
      : 'Выбранная область будет видна на странице профиля';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl overflow-hidden p-0 sm:rounded-lg">
        <DialogHeader className="px-6 pt-6 pr-12">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {isPreview && previewUrl ? (
          <BannerPreviewPanel previewUrl={previewUrl} />
        ) : (
          <div className="relative h-[360px] w-full bg-black sm:h-[420px]">
            {variant === 'avatar' ? (
              <AvatarCropStage
                imageSrc={imageSrc}
                rotation={rotation}
                onSelectionPixelsChange={setSelectionPixels}
              />
            ) : (
              <BannerCropStage
                imageSrc={imageSrc}
                rotation={rotation}
                aspect={aspect || PROFILE_BANNER_CROP_ASPECT}
                onSelectionPixelsChange={setSelectionPixels}
              />
            )}

            <div className="absolute bottom-3 right-3 z-20 flex gap-1 rounded-md bg-black/55 p-1">
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="h-8 w-8 text-white hover:bg-white/10 hover:text-white"
                disabled={isSubmitting}
                aria-label="Повернуть против часовой"
                onClick={() => setRotation((prev) => (prev - 90 + 360) % 360)}
              >
                <RotateCcw className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="h-8 w-8 text-white hover:bg-white/10 hover:text-white"
                disabled={isSubmitting}
                aria-label="Повернуть по часовой"
                onClick={() => setRotation((prev) => (prev + 90) % 360)}
              >
                <RotateCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {error ? (
          <div className="px-6 pt-3">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        ) : null}

        <DialogFooter
          className={cn(
            'gap-2 px-6 py-4',
            variant === 'avatar'
              ? 'flex-col-reverse sm:flex-row sm:justify-center'
              : 'flex-col sm:flex-row sm:justify-between',
          )}
        >
          {variant === 'avatar' ? (
            <>
              <Button
                type="button"
                disabled={isSubmitting}
                onClick={() => void handleDone()}
                className="min-w-[200px]"
              >
                {isSubmitting ? 'Сохранение…' : 'Сохранить и продолжить'}
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={isSubmitting}
                onClick={() => onOpenChange(false)}
                className="min-w-[200px]"
              >
                Вернуться назад
              </Button>
            </>
          ) : (
            <>
              <div className="flex w-full flex-wrap gap-2 sm:w-auto">
                {!isPreview ? (
                  <Button
                    type="button"
                    variant="outline"
                    disabled={isSubmitting || isBuildingPreview}
                    onClick={() => void handlePreview()}
                  >
                    {isBuildingPreview ? 'Предпросмотр…' : 'Предпросмотр'}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    disabled={isSubmitting}
                    onClick={() => setIsPreview(false)}
                  >
                    Вернуться к редактированию
                  </Button>
                )}
              </div>
              <div className="flex w-full justify-end gap-2 sm:w-auto">
                <Button
                  type="button"
                  variant="outline"
                  disabled={isSubmitting}
                  onClick={() => onOpenChange(false)}
                >
                  Отмена
                </Button>
                <Button type="button" disabled={isSubmitting} onClick={() => void handleDone()}>
                  {isSubmitting ? 'Сохранение…' : 'Установить фон'}
                </Button>
              </div>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
